import { ChangeDetectionStrategy, Component, Input, OnInit, signal } from '@angular/core';
import { UrlTree } from '@angular/router';
import { IconName } from '@fortawesome/fontawesome-common-types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { PermissionOptions } from 'src/app/modules/authentication/directives/permission.directive';
import { AuthenticationService } from 'src/app/modules/authentication/services/authentication.service';
import { hasItems } from 'src/app/shared/util/array-helper.util';

export interface SubmenuItem {
  label: string;
  link?: string | unknown[] | UrlTree | null;
  permissions?: PermissionOptions;
  isHeader?: boolean;
  items?: SubmenuItem[];
}

@UntilDestroy()
@Component({
  selector: 'app-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrls: ['./menu-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuItemComponent implements OnInit {
  @Input() iconSizeOverride?: string | number;
  @Input() iconName?: IconName;
  @Input() title?: string;
  @Input() link?: string | unknown[] | UrlTree | null;
  @Input() submenuItems: SubmenuItem[] = [];
  submenuItemsToRender = signal<SubmenuItem[]>([]);

  constructor(private authService: AuthenticationService) {}

  ngOnInit(): void {
    this.filterSubmenuItems();
  }

  private filterSubmenuItems(): void {
    this.filterItems$(this.submenuItems)
      .pipe(untilDestroyed(this))
      .subscribe((filteredItems) => {
        this.submenuItemsToRender.set(filteredItems);
        this.updateMenuItem();
      });
  }

  private filterItems$(items: SubmenuItem[]): Observable<SubmenuItem[]> {
    if (!hasItems(items)) {
      return of([]);
    }
    return combineLatest(
      items.map((item) =>
        this.authService
          .hasPermissions$(item.permissions?.permissions ?? [], item.permissions?.inclusivity ?? 'some')
          .pipe(map((hasPermission) => (hasPermission ? item : null)))
      )
    ).pipe(
      map((results) => results.filter((item): item is SubmenuItem => item != null)),
      switchMap((filtered) => {
        if (filtered.length === 1 && filtered[0].isHeader && hasItems(filtered[0].items)) {
          return this.filterItems$(filtered[0].items);
        }
        return this.filterHeaderChildren$(filtered);
      })
    );
  }

  private filterHeaderChildren$(items: SubmenuItem[]): Observable<SubmenuItem[]> {
    const hasHeaders = items.some((item) => item.isHeader && hasItems(item.items));
    if (!hasHeaders) {
      return of(items);
    }
    return combineLatest(
      items.map((item) =>
        item.isHeader && hasItems(item.items)
          ? this.filterItems$(item.items).pipe(map((children) => ({ ...item, items: children })))
          : of(item)
      )
    );
  }

  private updateMenuItem(): void {
    const items = this.submenuItemsToRender();
    const firstItem = items[0];
    if (!firstItem) {
      return;
    }
    if (items.length === 1) {
      this.title = firstItem.label;
      this.link = firstItem.link;
    } else {
      const firstLink = this.findFirstLink(items);
      this.link = firstLink?.link;
    }
  }

  private findFirstLink(items: SubmenuItem[]): SubmenuItem | undefined {
    for (const item of items) {
      if (item.link != null) return item;
      if (hasItems(item.items)) {
        const found = this.findFirstLink(item.items);
        if (found) return found;
      }
    }
    return undefined;
  }
}
